import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../../../shared/components/Button.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import {
  listGroupings,
  loadBrandingDashboard,
  type BrandingDashboardRow,
  type DashboardFilters,
} from '../../api/brandingDashboardApi.js';
import {
  listPlanningCycles,
  currentCycleId,
  type PlanningCycle,
} from '../../api/vendorBrandingProfileApi.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

const LABELS: Record<string, string> = {
  BRANDING_TYPE: 'Branding type',
  BRANDING_ITEM: 'Sub-type',
  GEO_ZONE: 'Zone',
  GEO_REGION: 'Region',
  GEO_STATE: 'State',
  GEO_DISTRICT: 'District',
  GEO_TEHSIL: 'Tehsil',
  GEO_CITY: 'City',
  GEO_TERRITORY: 'Territory',
  VENDOR: 'Vendor',
};

interface Crumb {
  /** The axis in view when this drill was made — where Back returns to. */
  readonly groupBy: string;
  /** The axis the drill moved to, so a crumb restores the view it names. */
  readonly nextGroupBy: string;
  readonly memberId: string;
  readonly memberName: string;
}

/** Which filter a drill-down on this axis sets. */
function filterKeyFor(groupBy: string): keyof DashboardFilters | null {
  if (groupBy === 'BRANDING_TYPE') return 'brandingTypeId';
  if (groupBy === 'BRANDING_ITEM') return 'brandingItemId';
  if (groupBy === 'VENDOR') return 'vendorId';
  if (groupBy.startsWith('GEO_')) return 'geoNodeId';
  return null;
}

/**
 * Branding tracking — budget against committed work-order value.
 *
 * The axis is chosen, not fixed: branding type, sub-type, any geography level
 * the tenant uses, or vendor. Clicking a row keeps its filter and re-groups on
 * the next axis, so the same screen answers "ATL vs BTL", "wall painting in
 * Jaipur" and "what has this vendor been given" without a report per question.
 */
export function BrandingTrackingDashboardPage(): ReactNode {
  const { token } = useAuth();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money, decimal } = useBusinessNumber();
  const [groupings, setGroupings] = useState<readonly string[]>([]);
  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  const [groupBy, setGroupBy] = useState('BRANDING_TYPE');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [crumbs, setCrumbs] = useState<readonly Crumb[]>([]);
  const [rows, setRows] = useState<readonly BrandingDashboardRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Kept apart from `error`: the dashboard read clears that on success, and
  // must not wipe the reason a filter has nothing in it.
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      listGroupings(token).catch((e) => {
        if (alive) setLookupError(`Groupings could not be loaded: ${(e as Error).message}`);
        return [] as readonly string[];
      }),
      listPlanningCycles(token).catch((e) => {
        if (alive) setLookupError(`Financial years could not be loaded: ${(e as Error).message}`);
        return [] as readonly PlanningCycle[];
      }),
    ]).then(([g, c]) => {
      if (!alive) return;
      setGroupings(g);
      setCycles(c);
      setCycleId((p) => p || currentCycleId(c));
    });
    return () => { alive = false; };
  }, [token]);

  const filters = useMemo<DashboardFilters>(() => {
    const f: Record<string, string> = {};
    if (cycleId) f.planningCycleId = cycleId;
    if (fromDate) f.fromDate = fromDate;
    if (toDate) f.toDate = toDate;
    for (const c of crumbs) {
      const key = filterKeyFor(c.groupBy);
      if (key) f[key] = c.memberId;
    }
    return f as DashboardFilters;
  }, [cycleId, fromDate, toDate, crumbs]);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    loadBrandingDashboard(groupBy, filters, token)
      .then((r) => { if (alive) { setRows(r); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [groupBy, filters, token]);

  const drillInto = (row: BrandingDashboardRow) => {
    if (!row.memberId || !filterKeyFor(groupBy)) return;
    // Sub-type is the natural next step from a type; otherwise stay put and let
    // the user pick the axis, because there is no single correct order.
    const next =
      groupBy === 'BRANDING_TYPE' ? 'BRANDING_ITEM'
      : groupBy === 'BRANDING_ITEM' ? 'GEO_STATE'
      : groupBy === 'GEO_STATE' ? 'GEO_DISTRICT'
      : groupBy === 'GEO_DISTRICT' ? 'GEO_TEHSIL'
      : 'VENDOR';
    setCrumbs((c) => [
      ...c,
      { groupBy, nextGroupBy: next, memberId: row.memberId!, memberName: row.memberName },
    ]);
    setGroupBy(next);
  };

  /**
   * One step back out of the drill.
   *
   * The trail could be shortened but never walked back: a crumb dropped the
   * filters below it and left the axis where it was, so the deepest level
   * stayed on screen and the last crumb did nothing at all. The axis each
   * drill came from is now carried on the crumb, so both the trail and the
   * view return together.
   */
  const goBack = () => {
    const last = crumbs[crumbs.length - 1];
    if (!last) return;
    setCrumbs((c) => c.slice(0, -1));
    setGroupBy(last.groupBy);
  };

  const totals = (rows ?? []).reduce(
    (a, r) => ({
      budget: a.budget + Number(r.budgetAmount || 0),
      committed: a.committed + Number(r.committedAmount || 0),
    }),
    { budget: 0, committed: 0 },
  );

  return (
    <main aria-label="branding-tracking">
      <h1>Branding tracking</h1>
      {lookupError ? <p role="alert">{lookupError}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      <label htmlFor="bt-cycle">Financial year</label>
      <select id="bt-cycle" value={cycleId} onChange={(e) => setCycleId(e.target.value)} data-testid="filter-cycle">
        <option value="">All years</option>
        {cycles.map((c) => (
          <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleName}</option>
        ))}
      </select>

      <label htmlFor="bt-from">From</label>
      <DateInput id="bt-from" type="date" value={fromDate} onValueChange={(nextValue) => setFromDate(nextValue)} data-testid="filter-from" />
      <label htmlFor="bt-to">To</label>
      <DateInput id="bt-to" type="date" value={toDate} onValueChange={(nextValue) => setToDate(nextValue)} data-testid="filter-to" />

      <label htmlFor="bt-group">Group by</label>
      <select id="bt-group" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} data-testid="filter-groupby">
        {groupings.map((g) => <option key={g} value={g}>{LABELS[g] ?? g}</option>)}
      </select>

      <nav aria-label="drill-path">
        <Button
          type="button"
          variant="subtle"
          onClick={goBack}
          disabled={crumbs.length === 0}
          data-testid="drill-back"
        >
          ← Back
        </Button>
        <Button
          type="button"
          variant="subtle"
          onClick={() => { setCrumbs([]); setGroupBy('BRANDING_TYPE'); }}
          data-testid="crumb-reset"
        >
          All
        </Button>
        {crumbs.map((c, i) => (
          <Button
            key={`${c.groupBy}-${c.memberId}`}
            type="button"
            variant="subtle"
            onClick={() => {
              setCrumbs((all) => all.slice(0, i + 1));
              setGroupBy(c.nextGroupBy);
            }}
            data-testid={`crumb-${i}`}
          >
            {LABELS[c.groupBy] ?? c.groupBy}: {c.memberName}
          </Button>
        ))}
      </nav>

      <p data-testid="totals">
        Budget {money(Number(totals.budget))} · Committed {money(Number(totals.committed))} ·
        {' '}Variance {money(Number(totals.budget - totals.committed))}
      </p>

      {busy ? <p role="status">Loading…</p> : null}

      <table aria-label="tracking-table">
        <thead>
          <tr>
            <th>{LABELS[groupBy] ?? groupBy}</th>
            <th>Budget</th>
            <th>Committed</th>
            <th>Quantity</th>
            <th>Orders</th>
            <th>Variance</th>
            <th>Utilised %</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(rows ?? []).length === 0 && !busy ? (
            <tr><td colSpan={8} data-testid="no-rows">Nothing recorded for this selection.</td></tr>
          ) : null}
          {(rows ?? []).map((r, i) => (
            <tr key={`${r.memberId ?? 'none'}-${i}`} data-testid={`row-${i}`}>
              <td>{r.memberName}</td>
              <td>{money(Number(r.budgetAmount || 0))}</td>
              <td>{money(Number(r.committedAmount || 0))}</td>
              <td>{decimal(r.committedQuantity)} {r.unitOfMeasure ?? ''}</td>
              <td>{decimal(r.workOrderCount)}</td>
              <td>{money(Number(r.varianceAmount || 0))}</td>
              <td>{r.utilisationPct === null || r.utilisationPct === undefined ? '—' : decimal(r.utilisationPct)}</td>
              <td>
                {r.memberId ? (
                  <Button
                    type="button"
                    variant="subtle"
                    onClick={() => drillInto(r)}
                    data-testid={`drill-${i}`}
                  >
                    Drill in
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
